import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { logout } from "./authSlice";
import { sharedApi, getAuthToken } from "../../services/sharedApi";
import { toApiError } from "../../utils/errorUtils";

import type {
  LearningHome,
  PracticeActivityInput,
  PracticeAssessment,
  PracticeMemory,
} from "@lingoshade/domain";

interface LearningState {
  home: LearningHome | null;
  memory: PracticeMemory | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  activityError: boolean;
  homeRequest: string | null;
  memoryRequest: string | null;
  memoryError: boolean;
  assessmentRequest: string | null;
  goalSaving: boolean;
  goalError: boolean;
}
const initialState: LearningState = {
  home: null,
  memory: null,
  loading: false,
  saving: false,
  error: null,
  activityError: false,
  homeRequest: null,
  memoryRequest: null,
  memoryError: false,
  assessmentRequest: null,
  goalSaving: false,
  goalError: false,
};

export const fetchLearningHome = createAsyncThunk<
  LearningHome,
  void,
  { rejectValue: string }
>(
  "learning/home",
  async (
    _,
    { rejectWithValue },
  ): Promise<LearningHome | ReturnType<typeof rejectWithValue>> => {
    try {
      return await sharedApi.learning.home(
        getAuthToken(),
        Intl.DateTimeFormat().resolvedOptions().timeZone,
      );
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error).message);
    }
  },
);
export const fetchPracticeMemory = createAsyncThunk<
  PracticeMemory,
  string,
  { rejectValue: string }
>(
  "learning/memory",
  async (
    textId,
    { rejectWithValue },
  ): Promise<PracticeMemory | ReturnType<typeof rejectWithValue>> => {
    try {
      return await sharedApi.learning.memory(textId, getAuthToken());
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error).message);
    }
  },
);
export const recordPracticeActivity = createAsyncThunk<
  PracticeMemory,
  PracticeActivityInput,
  { rejectValue: string }
>(
  "learning/record",
  async (
    input,
    { rejectWithValue },
  ): Promise<PracticeMemory | ReturnType<typeof rejectWithValue>> => {
    try {
      return await sharedApi.learning.record(input, getAuthToken());
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error).message);
    }
  },
);
export const assessPractice = createAsyncThunk<
  { textId: string; assessment: PracticeAssessment },
  { textId: string; assessment: PracticeAssessment },
  { rejectValue: string }
>(
  "learning/assess",
  async (
    input,
    { rejectWithValue },
  ): Promise<
    | { textId: string; assessment: PracticeAssessment }
    | ReturnType<typeof rejectWithValue>
  > => {
    try {
      await sharedApi.learning.assess(
        input.textId,
        input.assessment,
        getAuthToken(),
      );
      return input;
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error).message);
    }
  },
);

export const saveLearningGoal = createAsyncThunk<
  number | null,
  number | null,
  { rejectValue: string }
>(
  "learning/goal",
  async (
    days,
    { rejectWithValue },
  ): Promise<number | null | ReturnType<typeof rejectWithValue>> => {
    try {
      await sharedApi.learning.goal(days, getAuthToken());
      return days;
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error).message);
    }
  },
);

const slice = createSlice({
  name: "learning",
  initialState,
  reducers: { resetLearning: (): LearningState => initialState },
  extraReducers: (builder): void => {
    builder
      .addCase(saveLearningGoal.pending, (state): void => {
        state.goalSaving = true;
        state.goalError = false;
      })
      .addCase(saveLearningGoal.fulfilled, (state, action): void => {
        state.goalSaving = false;
        if (state.home) state.home.weeklyGoalDays = action.payload;
      })
      .addCase(saveLearningGoal.rejected, (state): void => {
        state.goalSaving = false;
        state.goalError = true;
      })
      .addCase(logout, (): LearningState => initialState)
      .addCase(fetchLearningHome.pending, (state, action): void => {
        state.loading = true;
        state.home = null;
        state.error = null;
        state.homeRequest = action.meta.requestId;
      })
      .addCase(fetchLearningHome.fulfilled, (state, action): void => {
        if (state.homeRequest !== action.meta.requestId) return;
        state.home = action.payload;
        state.loading = false;
      })
      .addCase(fetchLearningHome.rejected, (state, action): void => {
        if (state.homeRequest !== action.meta.requestId) return;
        state.error = action.payload ?? "unavailable";
        state.loading = false;
      })
      .addCase(fetchPracticeMemory.pending, (state, action): void => {
        state.memory = null;
        state.memoryRequest = action.meta.requestId;
        state.memoryError = false;
      })
      .addCase(fetchPracticeMemory.fulfilled, (state, action): void => {
        if (state.memoryRequest === action.meta.requestId)
          state.memory = action.payload;
      })
      .addCase(fetchPracticeMemory.rejected, (state, action): void => {
        if (state.memoryRequest === action.meta.requestId)
          state.memoryError = true;
      })
      .addCase(recordPracticeActivity.fulfilled, (state, action): void => {
        state.activityError = false;
        if (action.payload && state.memory?.textId === action.payload.textId) {
          state.memory.hasListenedOnce ||= action.payload.hasListenedOnce;
          state.memory.listenedFraction = Math.max(
            state.memory.listenedFraction,
            action.payload.listenedFraction,
          );
        }
      })
      .addCase(recordPracticeActivity.rejected, (state): void => {
        state.activityError = true;
      })
      .addCase(assessPractice.pending, (state, action): void => {
        state.saving = true;
        state.assessmentRequest = action.meta.requestId;
      })
      .addCase(assessPractice.fulfilled, (state, action): void => {
        if (state.assessmentRequest !== action.meta.requestId) return;
        state.saving = false;
        state.activityError = false;
        if (state.memory?.textId === action.payload.textId)
          state.memory.assessment = action.payload.assessment;
      })
      .addCase(assessPractice.rejected, (state, action): void => {
        if (state.assessmentRequest !== action.meta.requestId) return;
        state.saving = false;
        state.activityError = true;
      });
  },
});
export const { resetLearning } = slice.actions;
export default slice.reducer;
